/**
 * oc-match.ts
 * integracion-compras-cuentas-pagar (AP-14.3, D6) — closed vocabulary for the
 * Comprobante-vs-OrdenDeCompra match rule. Reconciled against the binding
 * spec (`specs/cuentas-pagar-ledger/spec.md` AP-14.3): exactly 7 codes, NOT
 * design.md's earlier 8-code `OC_*`-prefixed sketch — the spec merges both
 * moneda cases (unrecognized / mismatched) into a single
 * `MONEDA_NO_VERIFICABLE` code.
 *
 * Deliberately separate from `AP_ERROR_CODES` (dto.ts): every code here is a
 * NON-BLOCKING advertencia (AP-14.2, D1 — the match never rejects an alta),
 * so it does not belong in the error envelope namespace, which maps to an
 * HTTP error status. Guarded by a test asserting zero overlap.
 *
 * Spec: openspec/specs/cuentas-pagar-ledger/spec.md AP-14.3
 * Design: openspec/changes/integracion-compras-cuentas-pagar/design.md D6
 * Tasks: openspec/changes/integracion-compras-cuentas-pagar/tasks.md Phase 2, task 2.1
 */
import { z } from 'zod';

/** AP-14.3 — order = severidad decreciente; the array order is the UI display order (D4). */
export const OC_MATCH_ADVERTENCIA_CODES = [
  'ORDEN_COMPRA_NO_ENCONTRADA',
  'ORDEN_COMPRA_ANULADA_O_RECHAZADA',
  'ORDEN_COMPRA_PENDIENTE_CONFIRMACION',
  'PROVEEDOR_NO_COINCIDE',
  'MONEDA_NO_VERIFICABLE',
  'MONTO_ACUMULADO_EXCEDE_TOLERANCIA',
  'SIN_RECEPCION_REGISTRADA',
] as const;

export const OcMatchAdvertenciaCodeSchema = z.enum(OC_MATCH_ADVERTENCIA_CODES);
export type OcMatchAdvertenciaCode = (typeof OC_MATCH_ADVERTENCIA_CODES)[number];

/**
 * `detalle`: SOLO escalares que el usuario que carga el comprobante ya puede
 * ver. NUNCA el nombre/identidad del proveedor de la OC (el tesorero puede
 * no tener `sumi.proveedor.leer`) y NUNCA ningún campo en
 * `ORDEN_COMPRA_NO_ENCONTRADA` — esa advertencia debe ser byte-idéntica sea
 * la OC inexistente o de otro tenant (AP-14.4, no-leak de tenancy).
 */
export const OcMatchAdvertenciaSchema = z.object({
  code: OcMatchAdvertenciaCodeSchema,
  detalle: z.record(z.string()).optional(),
});
export type OcMatchAdvertencia = z.infer<typeof OcMatchAdvertenciaSchema>;

/** AP-9.2 / D2 — 2% del total de la OC. Override por tenant en sumi.config. */
export const DEFAULT_TOLERANCIA_MONTO_OC_PCT = 2;
/** D2 — piso absoluto: 1 unidad de la moneda. NO configurable por tenant. */
export const TOLERANCIA_MONTO_OC_PISO = 1;
